import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { ENDPOINTS } from "@/api/application-api";
import { useAuth } from "@/context/auth-context";
import { handleResponse } from "@/utils/errorResponseHandlers";

export interface ServiceProviderDetailField {
  label: string;
  value: string;
}

export interface IdentitySettings {
  organization_id: number;
  sso_enabled: boolean;
  directory_sync_enabled: boolean;
  jit_provisioning_enabled: boolean;
  require_sso: boolean;
  default_workspace_id: number | null;
  default_workspace_role_id: number | null;
  workos_organization_id: string | null;
  workos_sso_connection_id: string | null;
  workos_directory_id: string | null;
  protocol: string | null;
  sso_connection_status: string;
  directory_status: string;
  identity_policy_version: number;
  service_provider_details?: ServiceProviderDetailField[];
}

export interface IdentitySettingsResponse {
  feature_enabled: boolean;
  settings: IdentitySettings | null;
}

export interface UpdateIdentitySettingsBody {
  sso_enabled?: boolean;
  directory_sync_enabled?: boolean;
  require_sso?: boolean;
  jit_provisioning_enabled?: boolean;
  default_workspace_id?: number | null;
  default_workspace_role_id?: number | null;
  workos_organization_id?: string;
  workos_sso_connection_id?: string;
  workos_directory_id?: string;
}

/**
 * GET identity settings for an organization. Returns the feature flag
 * and the settings row when enabled. Returns 403 for non-owners; the
 * UI handles that by hiding the tab entry.
 */
export const useIdentitySettings = (organizationId: number | null) => {
  const userToken = useAuth()?.userToken;
  return useQuery<IdentitySettingsResponse>({
    queryKey: ["identity", "settings", organizationId],
    enabled: organizationId !== null && Boolean(userToken),
    queryFn: async () =>
      fetch(ENDPOINTS.identity.settings(organizationId!), {
        headers: { Authorization: `Bearer ${userToken ?? ""}` },
      }).then(handleResponse),
  });
};

export const useUpdateIdentitySettings = (organizationId: number) => {
  const userToken = useAuth()?.userToken;
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: UpdateIdentitySettingsBody) =>
      fetch(ENDPOINTS.identity.settings(organizationId), {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${userToken ?? ""}`,
        },
        body: JSON.stringify(body),
      }).then(handleResponse),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["identity", "settings", organizationId],
      });
    },
  });
};
